import { useEffect } from 'react';
import type { MetaTag } from '../lib/og';

/**
 * Client-side <head> manager — Wingspan cut 9 (FINAL_STATE §7 front door).
 *
 * Applies a page title, OG/Twitter meta tags, and a JSON-LD node to the live
 * document on SPA navigation, then restores the previous values on unmount so
 * navigating away doesn't leak stale card data. Managed nodes carry a
 * `data-managed-meta` marker so we only ever touch our own tags.
 *
 * NOTE (crawler caveat): most social link-unfurlers (Slack, iMessage, X,
 * Facebook) do NOT execute JS, so this runtime injection alone does not give
 * them cards. Crawler-facing OG/JSON-LD is server-rendered into the shell by
 * the Cloudflare Pages Functions in `functions/market/[id].ts` and
 * `functions/u/[handle].ts`, which reuse the same builders in `lib/og.ts`.
 * This hook is the progressive-enhancement layer for in-app navigation and
 * JS-executing crawlers (e.g. Googlebot).
 */
export function useDocumentMeta(
  meta: { title: string; tags: MetaTag[]; jsonLd?: Record<string, unknown> } | null,
): void {
  useEffect(() => {
    if (!meta) return;
    const previousTitle = document.title;
    document.title = meta.title;

    const created: Element[] = [];
    const head = document.head;

    for (const tag of meta.tags) {
      const el = document.createElement('meta');
      if (tag.property) el.setAttribute('property', tag.property);
      if (tag.name) el.setAttribute('name', tag.name);
      el.setAttribute('content', tag.content);
      el.setAttribute('data-managed-meta', '');
      head.appendChild(el);
      created.push(el);
    }

    if (meta.jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-managed-meta', '');
      script.textContent = JSON.stringify(meta.jsonLd);
      head.appendChild(script);
      created.push(script);
    }

    return () => {
      document.title = previousTitle;
      for (const el of created) el.remove();
    };
  }, [meta]);
}
